import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, userApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkles, Mail, Lock, User, Loader2, Phone, Shield, ArrowLeft, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Register = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    screenName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'client',
    referralSource: ''
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = verify email
  const [emailOtp, setEmailOtp] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const referralOptions = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'google', label: 'Google Search' },
    { value: 'friend_family', label: 'Friend/Family' },
    { value: 'affiliate', label: 'Affiliate/Referral Code' },
    { value: 'event', label: 'Event/Conference' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, screenName, email, phone, password, confirmPassword, userType, referralSource } = formData;

    // Specific field validation with clear error messages
    const missingFields = [];
    if (!name) missingFields.push('Full Name');
    if (!screenName) missingFields.push('Screen Name');
    if (!email) missingFields.push('Email');
    if (!phone) missingFields.push('Phone Number');
    if (!password) missingFields.push('Password');
    if (!referralSource) missingFields.push('How did you hear about us');

    if (missingFields.length > 0) {
      if (missingFields.length === 1) {
        toast.error(`Please enter your ${missingFields[0]}`);
      } else {
        toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      return;
    }

    // Age verification check
    if (!ageConfirmed) {
      toast.error('You must confirm you are 18 years or older to use Bookd');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const result = await register(email, password, name, userType, phone, screenName, referralSource);
      if (result?.user?.user_id) {
        setRegisteredUserId(result.user.user_id);
      }
      toast.success('Account created! Please verify your email.');
      // Send email OTP
      try {
        await authApi.sendEmailOtp(email);
        toast.info('Verification code sent to your email');
      } catch (otpErr) {
        console.log('OTP send notice:', otpErr);
        toast.info('Verification code would be sent in production');
      }
      // Move to verification step
      setStep(2);
    } catch (error) {
      // More specific error messages based on backend response
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        if (errorDetail.toLowerCase().includes('email') && errorDetail.toLowerCase().includes('registered')) {
          toast.error('This email is already registered. Please login or use a different email.');
        } else if (errorDetail.toLowerCase().includes('screen_name') || errorDetail.toLowerCase().includes('screen name')) {
          toast.error('This screen name is already taken. Please choose another.');
        } else {
          toast.error(errorDetail);
        }
      } else {
        toast.error('Unable to create account. Please check your information and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setVerifyingEmail(true);
    try {
      await authApi.verifyEmailOtp(formData.email, emailOtp);
      // Fetch updated user data to reflect email_verified: true
      try {
        const meResponse = await userApi.getMe();
        updateUser(meResponse.data);
      } catch (e) {
        // If fetch fails, still proceed but log it
        console.error('Failed to refresh user data:', e);
      }
      toast.success('Email verified!');
      navigate('/dashboard');
    } catch (error) {
      // Show error and stay on verification screen
      if (error.response?.status === 400) {
        toast.error('Invalid verification code. Please try again.');
      } else if (error.response?.status === 404) {
        toast.error('Verification code expired. Please request a new one.');
      } else {
        toast.error('Verification failed. Please try again.');
      }
      // User stays on this screen to retry
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleResendCode = async () => {
    setSendingOtp(true);
    try {
      await authApi.sendEmailOtp(formData.email);
      toast.success('Verification code resent to your email');
    } catch (error) {
      toast.info('Verification code would be sent in production');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Google signup failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Email Verification
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 hero-bg">
        <div className="hero-glow absolute inset-0" />
        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="auth-card rounded-3xl p-8">
            <button 
              onClick={() => setStep(1)} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to registration
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Verify Your Email
              </h2>
              <p className="text-muted-foreground mt-2">
                We sent a 6-digit code to <strong className="text-foreground">{formData.email}</strong>
              </p>
            </div>

            <div className="space-y-4 mt-6">
              <div>
                <Label htmlFor="emailOtp">Enter Verification Code</Label>
                <Input
                  id="emailOtp"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest mt-2 h-14"
                  data-testid="email-otp-input"
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Check your backend logs for the OTP code in development
                </p>
              </div>

              <Button 
                onClick={handleVerifyEmail} 
                disabled={verifyingEmail || emailOtp.length !== 6}
                className="w-full rounded-xl h-12 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                data-testid="verify-email-btn"
              >
                {verifyingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                Verify Email
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Did not receive the code?{' '}
                <button 
                  onClick={handleResendCode} 
                  disabled={sendingOtp}
                  className="text-primary hover:underline disabled:opacity-50"
                >
                  {sendingOtp ? 'Sending...' : 'Resend'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Registration Form
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20 hero-bg">
      <div className="hero-glow absolute inset-0" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="auth-card rounded-3xl p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            <Link to="/" className="inline-flex items-center justify-center mb-8">
              <img 
                src="/bookd-logo-b-text.png" 
                alt="Bookd" 
                className="h-40 sm:h-44 w-auto"
              />
            </Link>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Create Account
            </h1>
            <p className="text-muted-foreground mt-2">
              Join the community of amazing bookees
            </p>
          </div>

          {/* Google Signup */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 rounded-xl"
            onClick={loginWithGoogle}
            data-testid="google-signup-btn"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              or continue with email
            </span>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name * <span className="text-xs text-muted-foreground">(Private)</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="screenName">Screen Name * <span className="text-xs text-muted-foreground">(Visible to users)</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="screenName"
                  name="screenName"
                  type="text"
                  placeholder="DJ_Johnny, ArtistJane"
                  value={formData.screenName}
                  onChange={handleChange}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="register-screen-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10 h-12 rounded-xl"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 pr-10 h-12 rounded-xl"
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="register-confirm-password-toggle"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <Label>I want to</Label>
              <RadioGroup
                value={formData.userType}
                onValueChange={(value) => setFormData({ ...formData, userType: value })}
                className="flex flex-col sm:flex-row gap-3"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="client" id="client" data-testid="register-client-radio" />
                  <Label htmlFor="client" className="font-normal cursor-pointer">
                    Book Talent (Booker)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="talent" id="talent" data-testid="register-talent-radio" />
                  <Label htmlFor="talent" className="font-normal cursor-pointer">
                    Offer Services (Bookee)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="both" data-testid="register-both-radio" />
                  <Label htmlFor="both" className="font-normal cursor-pointer">
                    Both
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referralSource">How did you hear about us? *</Label>
              <Select
                value={formData.referralSource}
                onValueChange={(value) => setFormData({ ...formData, referralSource: value })}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="register-referral-source">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {referralOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 18+ Age Verification */}
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="ageConfirm"
                checked={ageConfirmed}
                onCheckedChange={(checked) => setAgeConfirmed(checked)}
                className="mt-0.5"
                data-testid="age-confirm-checkbox"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="ageConfirm"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  I confirm I am 18 years or older *
                </label>
                <p className="text-xs text-muted-foreground">
                  You must be at least 18 years old to use Bookd services
                </p>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl btn-glow"
              disabled={loading || !ageConfirmed}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
